/**
 * Coordinate mapping for the file-preview image viewer.
 *
 * The viewer renders the image flex-centered inside its container with
 * `max-width: 100%; max-height: 100%` (a contain fit that never upscales) and
 * applies `transform: translate(pan) scale(zoom)` — translate FIRST, so the pan
 * offset is in screen pixels and is not multiplied by the zoom factor. The
 * transform origin is the element center (CSS default).
 *
 * These pure functions convert between screen-space points and image-fraction
 * coordinates (0..1 across the natural image on each axis) under that exact
 * transform. Consumed by the positional-annotation overlay to place and read
 * comment anchors on the displayed image.
 */

export interface Point {
  readonly x: number;
  readonly y: number;
}

export interface Size {
  readonly width: number;
  readonly height: number;
}

export interface Box {
  readonly left: number;
  readonly top: number;
  readonly width: number;
  readonly height: number;
}

/**
 * The layout box the viewer's flex-centered, max-constrained image occupies at
 * zoom 1 (no transform): scaled down to fit the container when larger, rendered
 * at natural size when smaller (max-width/max-height never upscale), centered
 * on both axes. Returned in the container box's own coordinate space.
 */
export function fitImageBox(container: Box, natural: Size): Box {
  const scale = Math.min(1, container.width / natural.width, container.height / natural.height);
  const width = natural.width * scale;
  const height = natural.height * scale;
  return {
    left: container.left + (container.width - width) / 2,
    top: container.top + (container.height - height) / 2,
    width,
    height,
  };
}

/**
 * Map an image fraction (0..1 per axis over the natural image) to the on-screen
 * point where it renders under `translate(pan) scale(zoom)` about the base
 * box's center. Input/output share the base box's coordinate space; pan is in
 * screen pixels.
 */
export function imageFractionToScreen(fraction: Point, baseBox: Box, zoom: number, pan: Point): Point {
  const centerX = baseBox.left + baseBox.width / 2;
  const centerY = baseBox.top + baseBox.height / 2;
  return {
    x: centerX + pan.x + (fraction.x - 0.5) * baseBox.width * zoom,
    y: centerY + pan.y + (fraction.y - 0.5) * baseBox.height * zoom,
  };
}

/**
 * Inverse of {@link imageFractionToScreen}: map an on-screen point back to the
 * image fraction it addresses. Results may fall outside 0..1 when the point is
 * off the image — callers decide whether to clamp or reject.
 */
export function screenToImageFraction(screen: Point, baseBox: Box, zoom: number, pan: Point): Point {
  const centerX = baseBox.left + baseBox.width / 2;
  const centerY = baseBox.top + baseBox.height / 2;
  return {
    x: (screen.x - centerX - pan.x) / (baseBox.width * zoom) + 0.5,
    y: (screen.y - centerY - pan.y) / (baseBox.height * zoom) + 0.5,
  };
}
